import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const GraphContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GraphVisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
  min-width: 800px;
  min-height: 700px;
`;

const GraphTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const GraphCanvas = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 600px;
  min-width: 700px;
  position: relative;
  overflow: visible;
`;

const Node = styled.div`
  position: absolute;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => {
    if (props.$isStart) return '#51cf66';
    if (props.$isEnd) return '#ff6b6b';
    if (props.$isVisited) return '#4ecdc4';
    if (props.$isCurrent) return '#ffd43b';
    if (props.$isPath) return '#ffa500';
    return '#f8f9fa';
  }};
  border: 3px solid ${props => props.$isHighlighted ? '#ff6b6b' : 'rgba(255, 255, 255, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #333;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 10;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
`;

const Edge = styled.div`
  position: absolute;
  height: 2px;
  background: ${props => {
    if (props.$isHighlighted) return '#ff6b6b';
    if (props.$isPath) return '#ffa500';
    return 'rgba(255, 255, 255, 0.6)';
  }};
  transform-origin: left center;
  transition: all 0.3s ease;
  z-index: 5;
`;

const EdgeWeight = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  transform: translate(-50%, -50%);
  z-index: 15;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 2px solid ${props => props.$borderColor || 'transparent'};
`;

const Controls = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const ControlGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  background: ${props => props.primary ? '#51cf66' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;
  margin-bottom: 10px;
  
  &:hover:not(:disabled) {
    background: ${props => props.primary ? '#40c057' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AlgorithmInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
  height: fit-content;
`;

const InfoTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const InfoText = styled.p`
  margin: 0 0 15px 0;
  line-height: 1.6;
  opacity: 0.9;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const OperationStatus = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  text-align: center;
  color: ${props => props.success ? '#51cf66' : props => props.error ? '#ff6b6b' : 'white'};
  font-weight: 500;
`;

const QueueDisplay = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
`;

const QueueTitle = styled.h4`
  margin: 0 0 10px 0;
  text-align: center;
`;

const QueueItems = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const QueueItem = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 0.9rem;
  font-weight: bold;
`;

// Graph class for graph operations
class Graph {
  constructor() {
    this.nodes = new Map();
    this.edges = [];
  }

  addNode(id, x, y) {
    this.nodes.set(id, { id, x, y, neighbors: [] });
  }

  addEdge(from, to, weight = 1) {
    // Validate nodes exist before adding edge
    if (!this.nodes.has(from) || !this.nodes.has(to)) {
      console.error(`Cannot add edge ${from}-${to}: nodes don't exist`);
      return;
    }
    
    this.edges.push({ from, to, weight });
    this.nodes.get(from).neighbors.push({ to, weight });
    this.nodes.get(to).neighbors.push({ from, weight });
  }

  getNode(id) {
    return this.nodes.get(id);
  }

  getAllNodes() {
    return Array.from(this.nodes.values());
  }

  getAllEdges() {
    return this.edges;
  }
  
  // Validate graph structure
  validateGraph() {
    const errors = [];
    
    for (const [nodeId, node] of this.nodes) {
      if (!node.neighbors || !Array.isArray(node.neighbors)) {
        errors.push(`Node ${nodeId} has invalid neighbors: ${node.neighbors}`);
      }
      
      for (const neighbor of node.neighbors || []) {
        if (!neighbor || typeof neighbor.to !== 'string' || typeof neighbor.weight !== 'number') {
          errors.push(`Node ${nodeId} has invalid neighbor: ${JSON.stringify(neighbor)}`);
        }
        
        if (!this.nodes.has(neighbor.to)) {
          errors.push(`Node ${nodeId} references non-existent neighbor: ${neighbor.to}`);
        }
      }
    }
    
    return errors;
  }
}

function GraphPage() {
  // Create initial graph with data
  const createInitialGraph = () => {
    const initialGraph = new Graph();
    
    // Add nodes in a grid-like pattern
    const nodePositions = [
      { id: 'A', x: 100, y: 100 },
      { id: 'B', x: 250, y: 100 },
      { id: 'C', x: 400, y: 100 },
      { id: 'D', x: 100, y: 250 },
      { id: 'E', x: 250, y: 250 },
      { id: 'F', x: 400, y: 250 },
      { id: 'G', x: 100, y: 400 },
      { id: 'H', x: 250, y: 400 },
      { id: 'I', x: 400, y: 400 }
    ];

    nodePositions.forEach(({ id, x, y }) => {
      initialGraph.addNode(id, x, y);
    });

    // Add random edges
    const edges = [
      { from: 'A', to: 'B', weight: 4 },
      { from: 'A', to: 'D', weight: 2 },
      { from: 'B', to: 'C', weight: 3 },
      { from: 'B', to: 'E', weight: 5 },
      { from: 'C', to: 'F', weight: 2 },
      { from: 'D', to: 'E', weight: 1 },
      { from: 'D', to: 'G', weight: 6 },
      { from: 'E', to: 'F', weight: 4 },
      { from: 'E', to: 'H', weight: 3 },
      { from: 'F', to: 'I', weight: 5 },
      { from: 'G', to: 'H', weight: 2 },
      { from: 'H', to: 'I', weight: 1 }
    ];

    edges.forEach(({ from, to, weight }) => {
      initialGraph.addEdge(from, to, weight);
    });

    return initialGraph;
  };

  const [graph, setGraph] = useState(createInitialGraph());
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [isOperating, setIsOperating] = useState(false);
  const [currentAlgorithm, setCurrentAlgorithm] = useState('');
  const [visitedNodes, setVisitedNodes] = useState(new Set());
  const [currentNode, setCurrentNode] = useState(null);
  const [pathNodes, setPathNodes] = useState(new Set());
  const [pathEdges, setPathEdges] = useState(new Set());
  const [operationSpeed, setOperationSpeed] = useState(1000);
  const [operationCount, setOperationCount] = useState(0);
  const [lastOperation, setLastOperation] = useState('');
  const [queue, setQueue] = useState([]);
  const [startNode, setStartNode] = useState('');
  const [endNode, setEndNode] = useState('');
  const [stepCount, setStepCount] = useState(0);
  const [graphVersion, setGraphVersion] = useState(0);

  const generateRandomGraph = useCallback(() => {
    console.log('generateRandomGraph function called');
    
    try {
      const newGraph = new Graph();
      console.log('New Graph instance created');
      
      // Generate random number of nodes (between 6 and 12)
      const numNodes = Math.floor(Math.random() * 7) + 6; // 6 to 12 nodes
      console.log(`Generating ${numNodes} random nodes`);
      
      // Generate random node positions
      const nodePositions = [];
      const nodeIds = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').slice(0, numNodes);
      
      for (let i = 0; i < numNodes; i++) {
        // Random position within canvas bounds (considering padding and node size)
        const x = Math.floor(Math.random() * 600) + 50; // 50 to 650 (within 700px canvas)
        const y = Math.floor(Math.random() * 500) + 50; // 50 to 550 (within 600px canvas)
        
        // Ensure minimum distance between nodes
        let validPosition = true;
        for (const existingNode of nodePositions) {
          const distance = Math.sqrt((x - existingNode.x) ** 2 + (y - existingNode.y) ** 2);
          if (distance < 100) { // Increased minimum distance to 100px
            validPosition = false;
            break;
          }
        }
        
        if (validPosition) {
          nodePositions.push({ id: nodeIds[i], x, y });
        } else {
          // Try again with a different position
          i--;
          continue;
        }
      }
      
      // Add nodes to graph
      nodePositions.forEach(({ id, x, y }) => {
        newGraph.addNode(id, x, y);
      });
      console.log('Random nodes added to graph:', nodePositions.map(n => `${n.id}(${n.x},${n.y})`));
      console.log('Canvas dimensions: 700x600px, Node positions within bounds');

      // Generate random edges
      const edges = [];
      const maxEdges = Math.floor(numNodes * 1.5); // Maximum edges = 1.5 * number of nodes
      const actualEdges = Math.floor(Math.random() * maxEdges) + Math.floor(numNodes / 2); // At least half the nodes should have edges
      
      console.log(`Generating ${actualEdges} random edges`);
      
      for (let i = 0; i < actualEdges; i++) {
        let attempts = 0;
        let edgeCreated = false;
        
        while (attempts < 50 && !edgeCreated) { // Limit attempts to avoid infinite loop
          const fromIndex = Math.floor(Math.random() * numNodes);
          const toIndex = Math.floor(Math.random() * numNodes);
          
          if (fromIndex !== toIndex) {
            const from = nodeIds[fromIndex];
            const to = nodeIds[toIndex];
            
            // Check if edge already exists
            const edgeExists = edges.some(edge => 
              (edge.from === from && edge.to === to) || 
              (edge.from === to && edge.to === from)
            );
            
            if (!edgeExists) {
              const weight = Math.floor(Math.random() * 10) + 1; // Random weight 1-10
              edges.push({ from, to, weight });
              edgeCreated = true;
            }
          }
          attempts++;
        }
      }
      
      // Add edges to graph
      edges.forEach(({ from, to, weight }) => {
        newGraph.addEdge(from, to, weight);
      });
      console.log('Random edges added to graph:', edges.map(e => `${e.from}-${e.to}(${e.weight})`));

      console.log('Generated random graph:', newGraph.getAllNodes().map(n => n.id));
      console.log('Graph edges:', newGraph.getAllEdges());
      
      // Update graph state
      setGraph(newGraph);
      console.log('Graph state updated');
      
      // Force a re-render by incrementing graph version
      setGraphVersion(prev => prev + 1);
      console.log('Graph version incremented');
      
      // Reset operation state
      setVisitedNodes(new Set());
      setCurrentNode(null);
      setPathNodes(new Set());
      setIsOperating(false);
      setQueue([]);
      setOperationCount(0);
      setLastOperation('');
      setStepCount(0);
      console.log('Operation state reset');
      
    } catch (error) {
      console.error('Error in generateRandomGraph:', error);
    }
  }, []);

  // Debug: Monitor graph state changes
  useEffect(() => {
    console.log('Graph state changed:', {
      nodeCount: graph.getAllNodes().length,
      edgeCount: graph.getAllEdges().length,
      nodes: graph.getAllNodes().map(n => n.id)
    });
  }, [graph]);

  const resetOperation = () => {
    setVisitedNodes(new Set());
    setCurrentNode(null);
    setPathNodes(new Set());
    setPathEdges(new Set());
    setIsOperating(false);
    setCurrentAlgorithm('');
    setQueue([]);
    setOperationCount(0);
    setLastOperation('');
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const calculateEdgePosition = (fromNode, toNode) => {
    const from = graph.getNode(fromNode);
    const to = graph.getNode(toNode);
    
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    const angle = Math.atan2(dy, dx);
    const startX = from.x + 25 * Math.cos(angle);
    const startY = from.y + 25 * Math.sin(angle);
    const endX = to.x - 25 * Math.cos(angle);
    const endY = to.y - 25 * Math.sin(angle);
    
    return {
      left: startX,
      top: startY,
      width: distance - 50,
      transform: `rotate(${angle}rad)`,
      weightX: (startX + endX) / 2,
      weightY: (startY + endY) / 2
    };
  };

  const bfs = async (startId) => {
    if (isOperating) return;
    
    // Validate start node exists
    if (!graph.getNode(startId)) {
      setLastOperation(`Error: Node '${startId}' not found in graph`);
      return;
    }
    
    setIsOperating(true);
    setCurrentAlgorithm('Breadth-First Search');
    setOperationCount(prev => prev + 1);
    setStepCount(0);
    
    const visited = new Set();
    const queue = [startId];
    const visitedOrder = [];
    
    setQueue([startId]);
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      setStepCount(prev => prev + 1);
      
      if (!visited.has(currentId)) {
        visited.add(currentId);
        visitedOrder.push(currentId);
        
        setVisitedNodes(new Set(visited));
        setCurrentNode(currentId);
        setQueue([...queue]);
        
        setLastOperation(`BFS Step ${stepCount + 1}: Visiting node ${currentId}. Queue: [${queue.join(', ')}]`);
        await delay(operationSpeed);
        
        const currentNode = graph.getNode(currentId);
        if (currentNode && currentNode.neighbors) {
          currentNode.neighbors.forEach(neighbor => {
            if (!visited.has(neighbor.to)) {
              queue.push(neighbor.to);
              setQueue([...queue]);
            }
          });
        }
      }
    }
    
    setLastOperation(`BFS completed. Visited ${visitedOrder.length} nodes: ${visitedOrder.join(' → ')}`);
    resetOperation();
  };

  const dfs = async (startId) => {
    if (isOperating) return;
    
    // Validate start node exists
    if (!graph.getNode(startId)) {
      setLastOperation(`Error: Node '${startId}' not found in graph`);
      return;
    }
    
        setIsOperating(true);
    setCurrentAlgorithm('Depth-First Search');
    setOperationCount(prev => prev + 1);
    setStepCount(0);
    
    const visited = new Set();
    const visitedOrder = [];
    
    const dfsRecursive = async (nodeId) => {
      if (visited.has(nodeId)) return;
      
      setStepCount(prev => prev + 1);
      visited.add(nodeId);
      visitedOrder.push(nodeId);
      
      setVisitedNodes(new Set(visited));
      setCurrentNode(nodeId);
      
              setLastOperation(`DFS Step ${stepCount + 1}: Visiting node ${nodeId}. Visited: [${visitedOrder.join(', ')}]`);
        await delay(operationSpeed);
        
        const currentNode = graph.getNode(nodeId);
      if (currentNode && currentNode.neighbors) {
        for (const neighbor of currentNode.neighbors) {
          if (!visited.has(neighbor.to)) {
            await dfsRecursive(neighbor.to);
          }
        }
      }
    };
    
    await dfsRecursive(startId);
    
    setLastOperation(`DFS completed. Visited ${visitedOrder.length} nodes: ${visitedOrder.join(' → ')}`);
    resetOperation();
  };

  const dijkstra = async (startId, endId) => {
    if (isOperating || !startId || !endId) return;
    
    // Validate start and end nodes exist
    if (!graph.getNode(startId)) {
      setLastOperation(`Error: Start node '${startId}' not found in graph`);
      return;
    }
    if (!graph.getNode(endId)) {
      setLastOperation(`Error: End node '${endId}' not found in graph`);
      return;
    }
    
    setIsOperating(true);
    setCurrentAlgorithm('Dijkstra\'s Shortest Path');
    setOperationCount(prev => prev + 1);
    
    try {
      const distances = new Map();
      const previous = new Map();
      const unvisited = new Set();
      
      // Initialize distances
      graph.getAllNodes().forEach(node => {
        distances.set(node.id, Infinity);
        unvisited.add(node.id);
      });
      distances.set(startId, 0);
      
      let iterationCount = 0;
      const maxIterations = graph.getAllNodes().length * 2; // Prevent infinite loops
      const startTime = Date.now();
      const maxTime = 30000; // 30 seconds timeout
      
      while (unvisited.size > 0 && iterationCount < maxIterations) {
        iterationCount++;
        
        // Check for timeout
        if (Date.now() - startTime > maxTime) {
          setLastOperation('Algorithm stopped: Timeout reached (30 seconds)');
          resetOperation();
          return;
        }
        
        // Find node with minimum distance
        let minNode = null;
        let minDistance = Infinity;
        
        for (const nodeId of unvisited) {
          if (distances.get(nodeId) < minDistance) {
            minDistance = distances.get(nodeId);
            minNode = nodeId;
          }
        }
        
        if (minNode === null) break;
        
        unvisited.delete(minNode);
        setVisitedNodes(new Set(Array.from(graph.getAllNodes().map(n => n.id)).filter(id => !unvisited.has(id))));
        setCurrentNode(minNode);
        
        setLastOperation(`Dijkstra Step ${iterationCount}: Visiting node ${minNode} (Distance: ${minDistance})`);
        await delay(operationSpeed);
        
        if (minNode === endId) {
          console.log('Destination reached! Reconstructing path...');
          
          // Reconstruct path
          const path = [];
          let current = endId;
          while (current !== null) {
            path.unshift(current);
            current = previous.get(current);
          }
          
          console.log('Path reconstructed:', path);
          console.log('Previous map:', Object.fromEntries(previous));
          
          // Highlight the final path
          console.log('Setting path nodes:', path);
          setPathNodes(new Set(path));
          
          // Create path edges for highlighting
          const pathEdgesSet = new Set();
          for (let i = 0; i < path.length - 1; i++) {
            const edgeKey1 = `${path[i]}-${path[i + 1]}`;
            const edgeKey2 = `${path[i + 1]}-${path[i]}`;
            pathEdgesSet.add(edgeKey1);
            pathEdgesSet.add(edgeKey2);
          }
          setPathEdges(pathEdgesSet);
          
          // Force a re-render to ensure highlighting is applied
          setGraphVersion(prev => prev + 1);
          
          console.log('Path edges set:', Array.from(pathEdgesSet));
          
          setLastOperation(`Shortest path found: ${path.join(' → ')} (Distance: ${minDistance})`);
          
          // Keep the path highlighted for a few seconds before resetting
          await delay(3000);
          
          // Reset operation but keep path highlighted
          setIsOperating(false);
          setCurrentAlgorithm('');
          setQueue([]);
          setOperationCount(prev => prev + 1);
          setStepCount(0);
          
          console.log('Dijkstra algorithm completed successfully');
          return;
        }
        
        const currentNode = graph.getNode(minNode);
        if (currentNode && currentNode.neighbors) {
          currentNode.neighbors.forEach(neighbor => {
            if (unvisited.has(neighbor.to)) {
              const newDistance = distances.get(minNode) + neighbor.weight;
              if (newDistance < distances.get(neighbor.to)) {
                distances.set(neighbor.to, newDistance);
                previous.set(neighbor.to, minNode);
              }
            }
          });
        }
      }
      
      if (iterationCount >= maxIterations) {
        setLastOperation('Algorithm stopped: Maximum iterations reached');
      } else {
        setLastOperation('No path found between the specified nodes');
      }
      
      resetOperation();
      
    } catch (error) {
      console.error('Error in Dijkstra algorithm:', error);
      setLastOperation(`Error: ${error.message}`);
      resetOperation();
    }
  };



  const handleNodeClick = (nodeId) => {
    if (selectedNodes.length === 0) {
      setSelectedNodes([nodeId]);
    } else if (selectedNodes.length === 1) {
      if (selectedNodes[0] !== nodeId) {
        setSelectedNodes([...selectedNodes, nodeId]);
      } else {
        setSelectedNodes([]);
      }
    } else {
      setSelectedNodes([nodeId]);
    }
  };

  return (
    <GraphContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Graph Algorithm Visualizer</Title>
        <Subtitle>Learn and visualize various graph algorithms with interactive animations</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <GraphVisualizationContainer>
            <GraphTitle>Graph Visualization</GraphTitle>
            <GraphCanvas key={`graph-${graph.getAllNodes().length}-${graph.getAllEdges().length}`}>
              {graph.getAllEdges().map((edge, index) => {
                const position = calculateEdgePosition(edge.from, edge.to);
                return (
                  <React.Fragment key={index}>
                    <Edge
                      style={{
                        left: position.left,
                        top: position.top,
                        width: position.width,
                        transform: position.transform
                      }}
                      $isHighlighted={currentNode === edge.from || currentNode === edge.to}
                      $isPath={pathEdges.has(`${edge.from}-${edge.to}`) || pathEdges.has(`${edge.to}-${edge.from}`)}
                    />
                    <EdgeWeight
                      style={{
                        left: position.weightX,
                        top: position.weightY
                      }}
                    >
                      {edge.weight}
                    </EdgeWeight>
                  </React.Fragment>
                );
              })}
              
              {graph.getAllNodes().map((node) => (
                <Node
                  key={node.id}
                  style={{
                    left: node.x - 25,
                    top: node.y - 25
                  }}
                  $isStart={startNode === node.id}
                  $isEnd={endNode === node.id}
                  $isVisited={visitedNodes.has(node.id)}
                  $isCurrent={currentNode === node.id}
                  $isPath={pathNodes.has(node.id)}
                  $isHighlighted={selectedNodes.includes(node.id)}
                  onClick={() => handleNodeClick(node.id)}
                >
                  {node.id}
                </Node>
              ))}
            </GraphCanvas>
            
            <Legend>
              <LegendItem>
                <LegendColor color="#f8f9fa" />
                <span>Unvisited</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#4ecdc4" />
                <span>Visited</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ffd43b" />
                <span>Current</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ffa500" />
                <span>Path</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#51cf66" $borderColor="#51cf66" />
                <span>Start</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff6b6b" $borderColor="#ff6b6b" />
                <span>End</span>
              </LegendItem>
            </Legend>
          </GraphVisualizationContainer>
          
          <Controls>
            <ControlGroup>
              <Label>Operation Speed:</Label>
              <Input
                type="range"
                min="500"
                max="3000"
                value={operationSpeed}
                onChange={(e) => setOperationSpeed(parseInt(e.target.value))}
                disabled={isOperating}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>{operationSpeed}ms</div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Start Node:</Label>
              <Input
                type="text"
                value={startNode}
                onChange={(e) => setStartNode(e.target.value.toUpperCase())}
                placeholder="Enter start node (e.g., A)"
                disabled={isOperating}
                maxLength={1}
              />
            </ControlGroup>
            
            <ControlGroup>
              <Label>End Node (for Dijkstra):</Label>
              <Input
                type="text"
                value={endNode}
                onChange={(e) => setEndNode(e.target.value.toUpperCase())}
                placeholder="Enter end node (e.g., I)"
                disabled={isOperating}
                maxLength={1}
              />
            </ControlGroup>
            
            <ControlGroup>
              <Label>Graph Traversal:</Label>
              <div>
                <Button 
                  onClick={() => bfs(startNode || 'A')} 
                  disabled={isOperating || !startNode}
                >
                  BFS
                </Button>
                <Button 
                  onClick={() => dfs(startNode || 'A')} 
                  disabled={isOperating || !startNode}
                >
                  DFS
                </Button>
              </div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Path Finding:</Label>
              <Button 
                onClick={() => dijkstra(startNode, endNode)} 
                disabled={isOperating || !startNode || !endNode}
              >
                Dijkstra
              </Button>
            </ControlGroup>
            

            
            <ControlGroup>
              <Button 
                onClick={() => {
                  console.log('Generate New Graph button clicked');
                  generateRandomGraph();
                }} 
                disabled={isOperating}
              >
                Generate New Graph
              </Button>
              

            </ControlGroup>
          </Controls>
        </LeftPanel>
        
        <AlgorithmInfo>
          <InfoTitle>Graph Algorithms</InfoTitle>
          
          <InfoText>
            <strong>Current Algorithm:</strong> {currentAlgorithm || 'Ready'}
          </InfoText>
          
          <InfoText>
            <strong>Graph Size:</strong> {graph.getAllNodes().length} nodes, {graph.getAllEdges().length} edges
          </InfoText>
          
          <InfoText>
            <strong>Available Algorithms:</strong>
          </InfoText>
          
          <InfoText>
            • <strong>BFS:</strong> Breadth-first traversal (level by level)
          </InfoText>
          
          <InfoText>
            • <strong>DFS:</strong> Depth-first traversal (explore as far as possible)
          </InfoText>
          
          <InfoText>
            • <strong>Dijkstra:</strong> Find shortest path between two nodes
          </InfoText>
          

          
          <StatsContainer>
            <StatBox>
              <StatValue>{operationCount}</StatValue>
              <StatLabel>Operations</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{visitedNodes.size}</StatValue>
              <StatLabel>Visited</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{stepCount}</StatValue>
              <StatLabel>Current Step</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {queue.length > 0 && (
            <QueueDisplay>
              <QueueTitle>Current Queue/Stack</QueueTitle>
              <QueueItems>
                {queue.map((item, index) => (
                  <QueueItem key={index}>{item}</QueueItem>
                ))}
              </QueueItems>
            </QueueDisplay>
          )}
          
          {lastOperation && (
            <OperationStatus 
              success={!lastOperation.includes('Error')}
              error={lastOperation.includes('Error')}
            >
              {lastOperation}
            </OperationStatus>
          )}
        </AlgorithmInfo>
      </MainContent>
    </GraphContainer>
  );
}

export default GraphPage;
